import { createColumnHelper } from "@tanstack/react-table"
import type { ColumnDef } from "@tanstack/react-table"
import type { EngagementTimeEntriesRow } from "@/types/report"

const columnHelper = createColumnHelper<EngagementTimeEntriesRow>()

export const engagementTimeEntriesColumns: ColumnDef<EngagementTimeEntriesRow, any>[] = [
  columnHelper.accessor("date", {
    header: "Date",
    size: 210,
    meta: { type: "date" },
  }),
  columnHelper.accessor("client", {
    header: "Client",
    size: 200,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("engagementName", {
    header: "Engagement",
    size: 220,
    meta: { type: "string" },
  }),
  columnHelper.accessor("engagementCode", {
    header: "Code",
    size: 120,
    meta: { type: "string" },
  }),
  columnHelper.accessor("task", {
    header: "Task",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("notes", {
    header: "Notes",
    size: 220,
    meta: { type: "string" },
    enableHiding: true,
  }),
  columnHelper.accessor("hours", {
    header: "Hours",
    size: 110,
    meta: { type: "number" },
  }),
  columnHelper.accessor("isBillable", {
    header: "Billable?",
    size: 120,
    meta: { type: "boolean" },
  }),
  columnHelper.accessor("isInvoiced", {
    header: "Invoiced?",
    size: 120,
    meta: { type: "boolean" },
  }),
  columnHelper.accessor("isApproved", {
    header: "Approved?",
    size: 120,
    meta: { type: "boolean" },
  }),
  columnHelper.accessor("firstName", {
    header: "First Name",
    size: 150,
    meta: { type: "string" },
  }),
  columnHelper.accessor("lastName", {
    header: "Last Name",
    size: 150,
    meta: { type: "string" },
  }),
  // An identifier that happens to be numeric — summing it is meaningless
  columnHelper.accessor("userProfileId", {
    header: "Profile ID",
    size: 120,
    meta: { type: "integer", excludeFromGroupTotal: true },
    enableHiding: true,
  }),
  columnHelper.accessor("jobTitle", {
    header: "Job Title",
    size: 180,
    meta: { type: "string" },
  }),
  columnHelper.accessor("workerType", {
    header: "Worker Type",
    size: 150,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("t4Type", {
    header: "T4 Type",
    size: 120,
    meta: { type: "string", uniqueValues: true },
    enableHiding: true,
  }),
  // This report's money has two sides, and the work order is allowed to bill and pay in different
  // currencies, so every money column below names which side it belongs to. Bill-side columns take
  // the default field; pay-side columns point at payCurrency.
  //
  // A rate, not an amount — summing it across a chain's time entries is meaningless
  columnHelper.accessor("billableRate", {
    header: "Bill Rate",
    size: 130,
    meta: { type: "currency", excludeFromGroupTotal: true },
  }),
  columnHelper.accessor("billableAmount", {
    header: "Bill Amount",
    size: 140,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("payRate", {
    header: "Pay Rate",
    size: 130,
    meta: { type: "currency", currencyField: "payCurrency", excludeFromGroupTotal: true },
  }),
  columnHelper.accessor("payAmount", {
    header: "Pay Amount",
    size: 140,
    meta: { type: "currency", currencyField: "payCurrency" },
  }),
  columnHelper.accessor("currency", {
    header: "Bill Currency",
    size: 130,
    meta: { type: "string", uniqueValues: true },
    enableHiding: true,
  }),
  columnHelper.accessor("payCurrency", {
    header: "Pay Currency",
    size: 130,
    meta: { type: "string", uniqueValues: true },
    enableHiding: true,
  }),
  // Carries the denomination of Reporting Bill Rate. Hidden like the other two, but it has to exist
  // as a column: the Excel export's money format is symbol-free, so a currency column is the only
  // place the file can say what a figure is in.
  columnHelper.accessor("engagementCurrency", {
    header: "Engagement Currency",
    size: 170,
    meta: { type: "string", uniqueValues: true },
    enableHiding: true,
  }),
  // Neither work order currency: this rate comes off the engagement's own worker record, so it
  // follows the engagement currency. On a CAD engagement billing in USD, the two disagree.
  columnHelper.accessor("reportingBillRate", {
    header: "Reporting Bill Rate",
    size: 160,
    meta: { type: "currency", currencyField: "engagementCurrency", excludeFromGroupTotal: true },
  }),
  // Per-day figures — a chain total of these would be a sum of daily rates, not a daily rate
  columnHelper.accessor("cogsPerDay", {
    header: "COGS / Day",
    size: 140,
    meta: { type: "currency", currencyField: "payCurrency", excludeFromGroupTotal: true },
  }),
  columnHelper.accessor("revenuePerDay", {
    header: "Revenue / Day",
    size: 150,
    meta: { type: "currency", excludeFromGroupTotal: true },
  }),
  // Revenue minus cost, so it spans both sides. The procedure returns null where the two currencies
  // differ, which renders as "—": there is no currency the figure could honestly be labelled with.
  columnHelper.accessor("gpPerDay", {
    header: "GP / Day",
    size: 130,
    meta: { type: "currency", excludeFromGroupTotal: true },
  }),
  columnHelper.accessor("procurementType", {
    header: "Procurement Type",
    size: 170,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("practice", {
    header: "Practice",
    size: 160,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("branch", {
    header: "Branch",
    size: 120,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("functionalManager", {
    header: "Functional Manager",
    size: 190,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("engagementLead", {
    header: "Engagement Lead",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("deliveryManager", {
    header: "Delivery Manager",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("workerLocation", {
    header: "Worker Location",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("engagementType", {
    header: "Engagement Type",
    size: 170,
    meta: { type: "string", uniqueValues: true },
  }),
  // The worker's own start/end on the engagement this entry belongs to, not the engagement's own
  // dates. Headers match Hours; People names its wider per-worker span differently.
  columnHelper.accessor("engagementStartDate", {
    header: "Engagement Start Date",
    size: 200,
    meta: { type: "date" },
  }),
  columnHelper.accessor("engagementEndDate", {
    header: "Engagement End Date",
    size: 190,
    meta: { type: "date" },
  }),
  // When the worker's employment ends, independent of any one engagement, matching the column of
  // this name on People.
  columnHelper.accessor("employmentEndDate", {
    header: "Employment End Date",
    size: 190,
    meta: { type: "date" },
  }),
  columnHelper.accessor("isUtilized", {
    header: "Utilised?",
    size: 120,
    meta: { type: "boolean" },
  }),
  columnHelper.accessor("isPto", {
    header: "PTO",
    size: 100,
    meta: { type: "boolean" },
  }),
  columnHelper.accessor("isStatHoliday", {
    header: "Stat Holiday",
    size: 130,
    meta: { type: "boolean" },
  }),
  columnHelper.accessor("isPayable", {
    header: "Payable",
    size: 110,
    meta: { type: "boolean" },
  }),
  columnHelper.accessor("timesheetApproverName", {
    header: "Timesheet Approver",
    size: 190,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("parentEngagementCode", {
    header: "Parent Engagement Code",
    size: 200,
    meta: { type: "string" },
  }),
  // GW-231: groups an engagement together with its change-request siblings, under the chain root.
  // Offered in addition to grouping by Engagement — this report is one row per time entry, so that
  // grouping is already meaningful and stays available. Unlike the Parent Engagement columns above,
  // which are the IMMEDIATE source, this walks the chain all the way to its root.
  // A grouping key rather than a data column, so it is hidden from the grid body by default.
  columnHelper.accessor("rollUpEngagement", {
    header: "Roll-up",
    size: 240,
    meta: { type: "string" },
    enableHiding: true,
  }),
  columnHelper.accessor("parentEngagementName", {
    header: "Parent Engagement Name",
    size: 220,
    meta: { type: "string" },
  }),
]

export const defaultHiddenColumns: Record<string, boolean> = {
  userProfileId: false,
  notes: false,
  t4Type: false,
  currency: false,
  payCurrency: false,
  engagementCurrency: false,
  rollUpEngagement: false,
}
